/**
 * 고객 목록 CSV (UTF-8 BOM, 엑셀 호환) — RFC 4180 수준 파싱/이스케이프
 */

/**
 * 내보내기/가져오기 공통 헤더 (순서 고정)
 * 고객 상세 페이지「기본 정보」+ 메모 블록의 라벨·순서와 동일
 */
export const CUSTOMER_CSV_HEADERS = [
  '이름',
  '전화번호',
  '고객 구분',
  '생년월일',
  '지역',
  '소속',
  '호칭',
  '집 주소',
  '회사 주소',
  '이메일',
  '고객 출처',
  '메모',
]

/** 헤더 셀 BOM·NBSP 제거 */
export function normalizeHeaderCell(h) {
  return String(h ?? '')
    .replace(/^\uFEFF/g, '')
    .trim()
    .replace(/\u00A0/g, ' ')
}

/**
 * 가져오기 전 첫 줄에 이름·전화 열이 있는지(인코딩/열 이름 확인용)
 * @param {string[]} headers
 */
export function csvImportHeadersLookUsable(headers) {
  const j = headers.map(normalizeHeaderCell).join(' ')
  const hasName = /이름|customerName|고객명/i.test(j)
  const hasPhone = /전화|연락|customerPhone|phone|휴대/i.test(j)
  return hasName && hasPhone
}

/**
 * 엑셀 "CSV(쉼표 분리)"는 종종 CP949(ANSI)라 UTF-8로 읽으면 헤더가 깨져 열 매칭이 안 됨.
 * UTF-8로 읽었을 때 헤더가 유효하지 않으면 windows-949/euc-kr로 재시도.
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function readCustomerImportFileAsText(file) {
  const buf = await file.arrayBuffer()
  const decode = (label) => {
    try {
      return new TextDecoder(label, { fatal: false }).decode(buf)
    } catch {
      return ''
    }
  }
  const utf8 = decode('utf-8')
  const { headers: hu } = parseCsv(utf8)
  if (csvImportHeadersLookUsable(hu)) return utf8

  for (const label of ['windows-949', 'euc-kr']) {
    const t = decode(label)
    if (!t) continue
    const { headers: hc } = parseCsv(t)
    if (csvImportHeadersLookUsable(hc)) return t
  }
  return utf8
}

/**
 * 엑셀이 CSV를 열 때 숫자열로 인식하면 앞자리 0이 사라짐(010… → 10…).
 * 셀 값을 `="010…"` 형태로 두면 문자열로 인식되어 0이 유지됨.
 * @param {unknown} rawDigits API·목록의 숫자만 또는 null
 * @returns {string}
 */
export function excelCsvPhoneCell(rawDigits) {
  const d = String(rawDigits == null ? '' : rawDigits).replace(/\D/g, '')
  if (!d) return ''
  return `="${d}"`
}

/**
 * CSV/엑셀에서 전화번호 셀이 숫자·과학적 표기법(1.01E+10)·소수(… .0)로 온 경우 숫자만 추출.
 */
export function digitsOnlyFromLoosePhoneCell(raw) {
  let t = String(raw ?? '').trim()
  if (!t) return ''
  if (/e/i.test(t)) {
    const n = Number(t.replace(/\s/g, ''))
    if (Number.isFinite(n) && n > 0) {
      const rounded = Math.round(n)
      if (rounded >= 1e8 && rounded < 1e12) {
        return String(rounded).replace(/\D/g, '')
      }
    }
  }
  if (/^\d+\.\d+$/.test(t)) {
    const n = Number(t)
    if (Number.isFinite(n) && n > 0) {
      return String(Math.round(n)).replace(/\D/g, '')
    }
  }
  return t.replace(/\D/g, '')
}

/** @param {string} s */
function parseTabDelimited(s) {
  const lines = s.split(/\r\n|\n|\r/)
  const rows = []
  for (const line of lines) {
    rows.push(line.split('\t'))
  }
  while (rows.length > 0 && rows[rows.length - 1].every((c) => String(c ?? '').trim() === '')) {
    rows.pop()
  }
  if (rows.length === 0) {
    return { headers: [], rows: [] }
  }
  const headers = rows[0].map((h) => normalizeHeaderCell(h))
  const dataRows = rows.slice(1).filter((r) => r.some((c) => String(c || '').trim() !== ''))
  return { headers, rows: dataRows }
}

/** 유럽/일부 엑셀 로캘: 세미콜론 구분 CSV */
function parseSemicolonDelimited(s) {
  const lines = s.split(/\r\n|\n|\r/)
  const rows = []
  for (const line of lines) {
    rows.push(line.split(';'))
  }
  while (rows.length > 0 && rows[rows.length - 1].every((c) => String(c ?? '').trim() === '')) {
    rows.pop()
  }
  if (rows.length === 0) {
    return { headers: [], rows: [] }
  }
  const headers = rows[0].map((h) => normalizeHeaderCell(h))
  const dataRows = rows.slice(1).filter((r) => r.some((c) => String(c || '').trim() !== ''))
  return { headers, rows: dataRows }
}

/** @param {unknown} v */
export function escapeCsvField(v) {
  const s = v == null ? '' : String(v)
  if (/[\r\n",]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/** @param {string[]} cells */
export function stringifyCsvRow(cells) {
  return cells.map(escapeCsvField).join(',')
}

/**
 * @param {string} text
 * @returns {{ headers: string[], rows: string[][] }}
 */
export function parseCsv(text) {
  const s = String(text || '').replace(/^\uFEFF/, '')
  const firstNl = s.search(/\r\n|\n|\r/)
  const firstLine = firstNl === -1 ? s : s.slice(0, firstNl)
  const tabCount = (firstLine.match(/\t/g) || []).length
  const commaCount = (firstLine.match(/,/g) || []).length
  const semiCount = (firstLine.match(/;/g) || []).length
  if (tabCount > commaCount && tabCount >= 1) {
    return parseTabDelimited(s)
  }
  if (semiCount > commaCount && semiCount >= 2) {
    return parseSemicolonDelimited(s)
  }

  const rows = []
  let row = []
  let cur = ''
  let i = 0
  let inQuotes = false

  const pushCell = () => {
    row.push(cur)
    cur = ''
  }
  const pushRow = () => {
    rows.push(row)
    row = []
  }

  while (i < s.length) {
    const c = s[i]
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          cur += '"'
          i += 2
          continue
        }
        inQuotes = false
        i += 1
        continue
      }
      cur += c
      i += 1
      continue
    }
    if (c === '"') {
      inQuotes = true
      i += 1
      continue
    }
    if (c === ',') {
      pushCell()
      i += 1
      continue
    }
    if (c === '\r') {
      if (s[i + 1] === '\n') i += 1
      pushCell()
      pushRow()
      i += 1
      continue
    }
    if (c === '\n') {
      pushCell()
      pushRow()
      i += 1
      continue
    }
    cur += c
    i += 1
  }
  pushCell()
  if (row.length > 0 && !(row.length === 1 && row[0] === '')) {
    rows.push(row)
  }

  if (rows.length === 0) {
    return { headers: [], rows: [] }
  }
  const headers = rows[0].map((h) => normalizeHeaderCell(h))
  const dataRows = rows.slice(1).filter((r) => r.some((c) => String(c || '').trim() !== ''))
  return { headers, rows: dataRows }
}

/** @param {string[]} headers @param {string[]} cells */
export function rowCellsToMap(headers, cells) {
  const m = {}
  headers.forEach((h, idx) => {
    const key = normalizeHeaderCell(h)
    const v = cells[idx] != null ? String(cells[idx]) : ''
    if (key) {
      m[key] = v
      const compact = key.replace(/\s+/g, '')
      if (compact && compact !== key && !Object.prototype.hasOwnProperty.call(m, compact)) {
        m[compact] = v
      }
    }
  })
  return m
}

/** @param {Record<string, string>} rowMap */
export function mapCustomerRowFromCsv(rowMap) {
  const g = (keys) => {
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(rowMap, k) && rowMap[k] != null) {
        return String(rowMap[k])
      }
    }
    return ''
  }
  return {
    customerName: g(['이름', 'customerName', '고객명']).trim(),
    customerPhone: digitsOnlyFromLoosePhoneCell(g(['전화번호', '연락처', 'customerPhone', '전화', '휴대폰'])),
    birthDateRaw: g(['생년월일', 'birthDate']).trim(),
    region: g(['지역', 'region']).trim(),
    customerSegment: g(['고객 구분', '고객구분', '세그먼트', 'customerSegment']).trim(),
    affiliation: g(['소속', 'affiliation']).trim(),
    honorific: g(['호칭', 'honorific']).trim(),
    homeAddress: g(['집 주소', '집주소', '자택주소', 'homeAddress']).trim(),
    companyAddress: g(['회사 주소', '회사주소', 'companyAddress']).trim(),
    customerEmail: g(['이메일', 'email', 'Email', 'customerEmail']).trim(),
    customerSource: g(['고객 출처', '고객출처', '유입경로', 'customerSource']).trim(),
    memo: g(['메모', 'memo']).trim(),
  }
}

/**
 * @param {string} filename
 * @param {string} content
 */
export function downloadUtf8BomFile(filename, content) {
  const bom = '\uFEFF'
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
